import React, { useState, useEffect, useRef } from "react";
import useRouting from "../hooks/useRouting";
import usePageTracking from "../hooks/usePageTracking";
import { trackEvent } from "../utils/trackEvent";
import environment from "../environment";
import axios from "axios";
import { getHeaders, getStorage } from "../utils/apiUtils";
import "./LoanTypeSelection.css";
import doctor_15876235 from "../assets/images/doctor_15876235.svg";
import Header from "./Header";

const formatIndianNumber = (value = "") => {
  if (!value) return "";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return "";
  // eslint-disable-next-line no-undef
  return new Intl.NumberFormat("en-IN").format(numeric);
};

const sanitizeToDigits = (value = "") => value.replace(/\D/g, "");

const LoanTypeSelection = () => {
  const [selectedLoanType, setSelectedLoanType] = useState("");
  const [treatmentTiming, setTreatmentTiming] = useState("");
  const [treatmentType, setTreatmentType] = useState("");
  const [customTreatmentType, setCustomTreatmentType] = useState("");
  const [treatmentCost, setTreatmentCost] = useState("");
  const { navigateWithContext } = useRouting();
  const hasFetchedRef = useRef(false);

  // Track page visit
  usePageTracking("loan-type");
  useEffect(() => { trackEvent("loan_type_selection_viewed"); }, []);

  // Fetch application data to prefill loan amount
  const fetchApplicationData = async () => {
    try {
      const applicationId = getStorage("application_id");
      if (!applicationId) {
        console.error("Application ID not found in localStorage");
        return;
      }

      const endpoint = `${environment.apiurl}applications/${applicationId}/view/data/?allowed=application`;
      const response = await axios.get(endpoint, { headers: getHeaders() });

      if (response?.data?.msg?.application) {
        const applicationData = response.data.msg.application;

        // Prefill loan amount if available
        if (applicationData.loan_amount_required) {
          setTreatmentCost(
            sanitizeToDigits(applicationData.loan_amount_required.toString())
          );
          setSelectedLoanType(applicationData.purpose_of_loan);
          setTreatmentTiming(applicationData.plan_treatment_schedule);
        }
      }
    } catch (error) {
      console.error("Error fetching application data:", error);
      // Don't show error to user for prefill failures
    }
  };

  useEffect(() => {
    // Prevent duplicate API calls in StrictMode
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;
    
    const forceMedical = localStorage.getItem("forceMedicalLoan");

    if (forceMedical === "true") {
      setSelectedLoanType("medical");
      localStorage.removeItem("forceMedicalLoan");
    }

    fetchApplicationData();
    fetchPersonalData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch personal data to prefill disease type
  const fetchPersonalData = async () => {
    try {
      const applicationId = getStorage("application_id");
      if (!applicationId) {
        console.error("Application ID not found in localStorage");
        return;
      }

      const endpoint = `${environment.apiurl}applications/${applicationId}/view/data/?allowed=personal`;
      const response = await axios.get(endpoint, { headers: getHeaders() });

      if (response?.data?.msg?.personal) {
        const personalData = response.data.msg.personal;

        // Prefill disease type if available
        if (personalData.disease_type) {
          const predefinedOptions = [
            "cervical",
            "dengue",
            "gallstones",
            "heart-arrhythmia",
          ];

          // Check if the disease type is in predefined options
          if (predefinedOptions.includes(personalData.disease_type)) {
            setTreatmentType(personalData.disease_type);
          } else {
            // If not in predefined options, set as "others" and populate custom field
            setTreatmentType("others");
            setCustomTreatmentType(personalData.disease_type);
          }
          // If disease type is set, assume medical loan
          // setSelectedLoanType("medical");
        }
      }
    } catch (error) {
      console.error("Error fetching personal data:", error);
      // Don't show error to user for prefill failures
    }
  };

  const handleLoanTypeChange = (type) => {
    setSelectedLoanType(type);
    localStorage.setItem("loanType", type);
    // Don't auto-navigate, wait for continue button
  };

  const handleTreatmentTimingChange = (e) => {
    setTreatmentTiming(e.target.value);
  };

  const handleTreatmentTypeChange = (e) => {
    setTreatmentType(e.target.value);
    // Clear custom input when switching away from "others"
    if (e.target.value !== "others") {
      setCustomTreatmentType("");
    }
  };

  const handleCustomTreatmentTypeChange = (e) => {
    setCustomTreatmentType(e.target.value);
  };

  const handleCostChange = (e) => {
    const value = sanitizeToDigits(e.target.value); // Only allow digits
    setTreatmentCost(value);
  };

  const handleContinue = async () => {
    // Validate form including custom treatment type when "others" is selected
    const isValidTreatmentType =
      treatmentType === "others" ? customTreatmentType.trim() : treatmentType;

    trackEvent("loan_type_continue_clicked", {
      loan_type: selectedLoanType,
      treatment_timing: treatmentTiming,
      treatment_type: treatmentType,
    });

    if (
      selectedLoanType &&
      treatmentTiming &&
      isValidTreatmentType &&
      treatmentCost
    ) {
      try {
        // Prepare API payload
        let payload = null;
        if (selectedLoanType === "medical") {
          payload = {
            data: [
              {
                model_name: "application",
                field: [
                  {
                    key: "loan_amount_required",
                    value: treatmentCost,
                  },
                  {
                    key: "purpose_of_loan",
                    value: selectedLoanType,
                  },
                  {
                    key: "plan_treatment_schedule",
                    value: treatmentTiming,
                  },
                ],
              },
              {
                model_name: "personal",
                field: [
                  {
                    key: "disease_type",
                    value:
                      treatmentType === "others"
                        ? customTreatmentType
                        : treatmentType,
                  },
                ],
              },
            ],
          };
        } else {
          payload = {
            data: [
              {
                model_name: "application",
                field: [
                  {
                    key: "loan_amount_required",
                    value: "",
                  },
                  {
                    key: "purpose_of_loan",
                    value: selectedLoanType,
                  },
                  {
                    key: "plan_treatment_schedule",
                    value: "",
                  },
                ],
              },
              {
                model_name: "personal",
                field: [
                  {
                    key: "disease_type",
                    value: "",
                  },
                ],
              },
            ],
          };
        }

        // Call API to save treatment details
        const applicationId = getStorage("application_id");
        await axios.post(
          `${environment.apiurl}applications/${applicationId}/save/data/`,
          payload,
          { headers: getHeaders() }
        );

        // Save to localStorage after successful API call
        localStorage.setItem(
          "treatmentDetails",
          JSON.stringify({
            loanType: selectedLoanType,
            timing: treatmentTiming,
            type:
              treatmentType === "others" ? customTreatmentType : treatmentType,
            cost: treatmentCost,
          })
        );

        if (selectedLoanType === "medical") {
          navigateWithContext("patient-info");
        } else {
          navigateWithContext("personal-loan-message");
        }
      } catch (error) {
        console.error("Error saving treatment details:", error);
        // Still navigate on error for better UX
        localStorage.setItem(
          "treatmentDetails",
          JSON.stringify({
            loanType: selectedLoanType,
            timing: treatmentTiming,
            type:
              treatmentType === "others" ? customTreatmentType : treatmentType,
            cost: treatmentCost,
          })
        );
        navigateWithContext("patient-info");
      }
    } else if (selectedLoanType === "personal") {
      navigateWithContext("personal-loan-message");
    }
  };





  return (
    <div className="loan-container">
      <Header showBackButton={false} />
      <div className="main-content">
        <div className="hero-section">
          <div className="hero-icon">
            <img src={doctor_15876235} alt="doctor" />
          </div>
          <h1 className="hero-title">Help us understand your need better</h1>
          <p className="hero-subtitle">
            So we can connect you to the
            <br />
            right care and financing options
          </p>
        </div>

        <div className="form-section">
          <div className="loan-type-card">
            <h2 className="form-title">Is this loan for medical treatment?</h2>
            <p className="form-subtitle">Choose the right option</p>

            <div className="radio-group">
              <div
                className={`radio-option radio-option-loan ${
                  selectedLoanType === "medical" ? "selected" : ""
                }`}
                onClick={() => handleLoanTypeChange("medical")}
              >
                <div className="card-content">
                  <div className="card-title">Yes, I need a medical loan</div>
                </div>
                <input
                  type="radio"
                  className="radio-input"
                  name="loanType"
                  value="medical"
                  checked={selectedLoanType === "medical"}
                  onChange={() => handleLoanTypeChange("medical")}
                />
                <label className="radio-label"></label>
              </div>

              <div
                className={`radio-option radio-option-loan ${
                  selectedLoanType === "personal" ? "selected" : ""
                }`}
                onClick={() => handleLoanTypeChange("personal")}
              >
                <div className="card-content">
                  <div className="card-title">No, I need a personal loan</div>
                </div>
                <input
                  type="radio"
                  className="radio-input"
                  name="loanType"
                  value="personal"
                  checked={selectedLoanType === "personal"}
                  onChange={() => handleLoanTypeChange("personal")}
                />
                <label className="radio-label"></label>
              </div>
            </div>
          </div>

          {/* Treatment Details Sections - Show only when Medical is selected */}
          {selectedLoanType === "medical" && (
            <div className="treatment-sections">
              {/* Section 1: When is your treatment planned? */}
              <div className="form-group">
                <h2 className="section-title">
                  When is your treatment planned?
                </h2>
                <div className="dropdown-container">
                  <label className="dropdown-label -ml-1">
                    Treatment planned within<span className="text-red-500">*</span> 
                  </label>
                  <select
                    className="custom-dropdown"
                    value={treatmentTiming}
                    onChange={handleTreatmentTimingChange}
                  >
                    <option value="">Choose the right option</option>
                    <option value="1-week">1 Week</option>
                    <option value="1-2-months">1 - 2 Months</option>
                    <option value="treatment-ongoing">Ongoing</option>
                    <option value="treatment-completed">
                      Completed
                    </option>
                  </select>
                </div>
              </div>

              {/* Section 2: What treatment do you need? */}
              <div className="form-group">
                <h2 className="section-title">What treatment do you need?</h2>
                <div className="dropdown-container">
                  <label className="dropdown-label -ml-[2px]">Disease <span className="text-red-500">*</span> </label>
                  <select
                    className="custom-dropdown"
                    value={treatmentType}
                    onChange={handleTreatmentTypeChange}
                  >
                    <option value="">Select disease</option>
                    <option value="IVF">IVF</option>
                    <option value="Dental Fracture">Dental Fracture</option>
                    <option value="Flap Surgery">Flap Surgery</option>
                    <option value="Root Canal">Root Canal</option>
                    <option value="ENT (Adenoidectomy)">
                      ENT (Adenoidectomy)
                    </option>
                    <option value="ENT (Fess Unilateral/Bilateral)">
                      ENT (Fess Unilateral/Bilateral)
                    </option>
                    <option value="ENT (Mastoidectomy)">
                      ENT (Mastoidectomy)
                    </option>
                    <option value="ENT (Tympanoplasty)">
                      ENT (Tympanoplasty)
                    </option>
                    <option value="Septoplasty">Septoplasty</option>
                    <option value="Sinus Surgery">Sinus Surgery</option>
                    <option value="Tonsillectomy">Tonsillectomy</option>
                    <option value="Appendix Removal (Appendectomy)">
                      Appendix Removal (Appendectomy)
                    </option>
                    <option value="Circumcision">Circumcision</option>
                    <option value="DJ Stent Removal">DJ Stent Removal</option>
                    <option value="Fissurectomy">Fissurectomy</option>
                    <option value="Sphincterotomy">Sphincterotomy</option>
                    <option value="Fistulectomy">Fistulectomy</option>
                    <option value="Gall Bladder Stone Removal (LapColi)">
                      Gall Bladder Stone Removal (LapColi)
                    </option>
                    <option value="Hernia (Incisional Hernioplasty)">
                      Hernia (Incisional Hernioplasty)
                    </option>
                    <option value="Hernia (Inguinal Hernioplasty)">
                      Hernia (Inguinal Hernioplasty)
                    </option>
                    <option value="Hernia (Umblical Hernioplasty)">
                      Hernia (Umblical Hernioplasty)
                    </option>
                    <option value="Hydrocele">Hydrocele</option>
                    <option value="Perianal Abscess">Perianal Abscess</option>
                    <option value="Piles Surgey (Hemorrhoidectomy)">
                      Piles Surgey (Hemorrhoidectomy)
                    </option>
                    <option value="Dilatation and Curettage ( D & C) ( DAY CARE)">
                      Dilatation and Curettage ( D & C) ( DAY CARE)
                    </option>
                    <option value="LSCS (For Delivery)">
                      LSCS (For Delivery)
                    </option>
                    <option value="Normal delivery (with well baby care)">
                      Normal delivery (with well baby care)
                    </option>
                    <option value="Ovarian Cystectomy">
                      Ovarian Cystectomy
                    </option>
                    <option value="Uterus Removal (Hysterectomy)">
                      Uterus Removal (Hysterectomy)
                    </option>
                    <option value="Cataract Surgery">Cataract Surgery</option>
                    <option value="Kidney stone removal (RIRS)">
                      Kidney stone removal (RIRS)
                    </option>
                    <option value="Prostate Removal (Radical Prostatectomy)">
                      Prostate Removal (Radical Prostatectomy)
                    </option>
                    <option value="others">Others</option>
                  </select>
                </div>

                {/* Custom treatment type input - shown when "Others" is selected */}
                {treatmentType === "others" && (
                  <div
                    className="dropdown-container"
                    style={{ marginTop: "16px" }}
                  >
                    <label className="dropdown-label -ml-[2px]">
                      Please specify the treatment type <span className="text-red-500">*</span> 
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Enter treatment type"
                      value={customTreatmentType}
                      onChange={handleCustomTreatmentTypeChange}
                    />
                  </div>
                )}
              </div>

              {/* Section 3: Treatment Cost */}
              <div className="form-group">
                <h2 className="section-title">Treatment Cost</h2>
                <div className="cost-input-container">
                  <label className="dropdown-label -ml-[2px]">
                    Enter Treatment Cost <span className="text-red-500">*</span> 
                  </label>
                  <div className="cost-input-wrapper">
                    <span className="currency-symbol">₹</span>
                    <input
                      type="text"
                      className="cost-input"
                      placeholder="10,000"
                      value={formatIndianNumber(treatmentCost)}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      onChange={handleCostChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="fixed-button-container">
        <button
          className="continue-button"
          onClick={handleContinue}
          disabled={
            selectedLoanType === "medical"
              ? !(
                  selectedLoanType &&
                  treatmentTiming &&
                  treatmentType &&
                  (treatmentType !== "others" || customTreatmentType.trim()) &&
                  treatmentCost
                )
              : !selectedLoanType
          }
        >
          CONTINUE
        </button>
      </div>
    </div>
  );
};

export default LoanTypeSelection;
